import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useEmailInvoice } from '@/hooks/useInvoices';
import type { Invoice } from '@/types/invoice';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EmailInvoiceDialogProps {
  invoice: Pick<
    Invoice,
    'id' | 'invoice_number' | 'customer_name' | 'customer_email' | 'total_amount' | 'amount_outstanding' | 'status' | 'payment_url'
  > | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Statuses that can be emailed. Cancelled invoices are the only exclusion. */
export function canEmailInvoice(invoice: { status: string } | null | undefined): boolean {
  return !!invoice && invoice.status !== 'cancelled';
}

/**
 * Email the itemised invoice to the customer (send-invoice-email). Paid
 * invoices go out as a receipt; unpaid ones show the balance and how to pay.
 * Changing the address here sends to it this once; edit the invoice to change
 * the bill-to email for good.
 */
export function EmailInvoiceDialog({ invoice, open, onOpenChange }: EmailInvoiceDialogProps) {
  const { formatMoney } = useCompanyLocale();
  const send = useEmailInvoice();
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (open) {
      setTo(invoice?.customer_email ?? '');
      setMessage('');
    }
  }, [open, invoice?.id, invoice?.customer_email]);

  const settled = invoice?.status === 'paid' || invoice?.status === 'refunded';
  const balance = Math.max(Number(invoice?.amount_outstanding ?? 0), 0);
  const trimmed = to.trim();
  const validTo = EMAIL_RE.test(trimmed);
  const overriding = !!invoice?.customer_email && trimmed.toLowerCase() !== invoice.customer_email.trim().toLowerCase();

  const handleSend = async () => {
    if (!invoice || !validTo) return;
    try {
      await send.mutateAsync({ invoiceId: invoice.id, to: trimmed, message });
      onOpenChange(false);
    } catch {
      // Toast handled by the hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !send.isPending && onOpenChange(next)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Email invoice</DialogTitle>
          <DialogDescription>
            {settled
              ? 'The customer gets the full invoice as a receipt, marked paid.'
              : 'The customer gets the full invoice with the amount due and how to pay.'}
          </DialogDescription>
        </DialogHeader>

        {invoice && (
          <div className="space-y-4">
            <div className="rounded-lg border p-3 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium">{invoice.invoice_number}</span>
                <span className="font-semibold tabular-nums">{formatMoney(invoice.total_amount)}</span>
              </div>
              <div className="text-muted-foreground">{invoice.customer_name}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {settled ? 'Paid in full' : `Amount due ${formatMoney(balance)}`}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email-invoice-to">Send to</Label>
              <Input
                id="email-invoice-to"
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="customer@example.com"
                autoComplete="off"
              />
              {trimmed && !validTo && <p className="text-xs text-destructive">Enter a valid email address.</p>}
              {overriding && validTo && (
                <p className="text-xs text-muted-foreground">
                  Sends to this address once. Edit the invoice to change its email for good.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email-invoice-message">Message (optional)</Label>
              <Textarea
                id="email-invoice-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Thanks again for having us out."
              />
            </div>

            {!settled && !invoice.payment_url && (
              <p className="text-xs text-muted-foreground">
                The email lists your bank details and payment instructions. To add a card pay link,
                use Send for payment first.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={send.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!invoice || !validTo || send.isPending}>
            {send.isPending ? 'Sending…' : 'Send email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
